import './index.css'

import React, { useCallback, useContext, useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'

import GoogleAnalytics from '@disco/api/ga'
import Mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  addToDisco,
  getSignedPublicPageUrl,
} from '@disco/state-management/actions/PublicPlaylistActions'
import { getNestingById } from '@disco/state-management/selectors/nesting'
import { getTracksByIds } from '@disco/state-management/selectors/tracks'
import {
  getAddToDiscoPublicObject,
  getAvailableDownloadLink,
  getDownloadOptions,
  getNestedTracksCount,
  getOriginalFormat,
  getOriginalFormatObject,
  getSaveToDiscoOptions,
  getViewPageLink,
} from '@disco/utils/tracks'

import type { PlaylistId } from 'types/playlist'
import type { Track } from 'types/track'
import type { PopoverItemType } from 'types/ui'

import { NestingVersionsContext } from 'contexts/nesting-versions'
import { ThemeContext } from 'contexts/theme'

import EllipsisMenu from 'components/ui/ellipsis-menu'
import Icon from 'components/ui/icon'

import { useDispatch, useSelector } from 'hooks/use-redux'

export type Props = {
  showSaveToDisco: boolean
  playlistId?: PlaylistId | null | undefined
  disableDownloads?: boolean
  zipMode?: 'original_else_optimized' | 'optimized'
  buttonSize?: 'default' | 'compact'
  campaignId?: number | null | undefined
  publicTrackItemRef?: React.RefObject<HTMLDivElement>
  recipientId?: number | null | undefined
  shareId: number | null | undefined
  track: Track
  onOptionClick?: () => void
  compact?: boolean
}

const TrackItemOptions = ({
  showSaveToDisco,
  playlistId,
  disableDownloads,
  zipMode,
  buttonSize,
  campaignId,
  publicTrackItemRef,
  recipientId,
  shareId,
  track,
  onOptionClick,
  compact,
}: Props) => {
  const dispatch = useDispatch()
  const tablet = useMediaQuery({
    maxWidth: 960,
  })
  const theme = useContext(ThemeContext).theme
  const nestingVersionsContext = useContext(NestingVersionsContext)
  const { nested_ids } =
    useSelector(state => getNestingById(state, track.id)) || {}
  const nestedTracks = useSelector(state =>
    getTracksByIds(state, {
      ids: nested_ids || [],
    }),
  )
  const nestedTracksCount: number = getNestedTracksCount(track)
  // we rely on the fact that backend sets ext for optimized format
  let ext = track.ext || ''
  let url = getAvailableDownloadLink(track, true)

  if (zipMode === 'original_else_optimized') {
    const originalExt = getOriginalFormat(track)
    const originalFormat = getOriginalFormatObject(track)

    if (originalExt && originalFormat) {
      ext = originalExt
      url = originalFormat.play_url
    }
  }

  const handleAddToDiscoClick = useCallback(
    nestedTracks => {
      const business = null
      const url = getSignedPublicPageUrl()
      const loginDetails = undefined
      const object = getAddToDiscoPublicObject(track, nestedTracks)
      let eventType = 'Regular'

      if (nestingVersionsContext?.nesting || nestedTracksCount) {
        eventType = track?.nesting_label
      }

      if (nestedTracks) {
        eventType = 'All versions'
      }

      let mixpanelEventDetails

      if (playlistId) {
        mixpanelEventDetails = {
          Context: 'Playlist page',
          Type: eventType,
          ['Playlist ID']: playlistId,
          ['Track ID']: track.id,
        }
      } else {
        mixpanelEventDetails = {
          Context: 'Track page',
          Type: eventType,
          ['Track ID']: track.id,
        }
      }

      dispatch(
        addToDisco(
          business,
          url,
          object,
          loginDetails,
          {
            campaignId,
            playlistId,
            recipientId,
            shareId,
          },
          mixpanelEventDetails,
        ),
      )
    },
    [campaignId, dispatch, playlistId, recipientId, shareId, track],
  )
  const handleDownloadClick = useCallback(
    extension => {
      api.sendTrackDownloadEvent(track.id, 'public-playlist-page', {
        campaign_id: campaignId,
        playlist_id: playlistId,
        recipient_id: recipientId,
        share_id: shareId,
      })
      GoogleAnalytics.sendEvent(
        'Formats/Downloads',
        'Download track from playlist page',
      )
      let mixpanelEventDetails

      if (playlistId) {
        mixpanelEventDetails = {
          Context: 'Playlist page',
          Type:
            nestingVersionsContext?.nesting || nestedTracksCount
              ? track.nesting_label
              : 'Regular',
          ['Playlist ID']: playlistId,
          ['Track ID']: track.id,
          Format: extension || track.ext,
        }
      } else {
        mixpanelEventDetails = {
          Context: 'Track page',
          Type:
            nestingVersionsContext?.nesting || nestedTracksCount
              ? track.nesting_label
              : 'Regular',
          ['Track ID']: track.id,
          Format: extension || track.ext,
        }
      }

      // Event #13 from https://disco-team.atlassian.net/browse/DISCO-4871
      Mixpanel.sendPureEvent('Download track', mixpanelEventDetails)
    },
    [campaignId, playlistId, recipientId, shareId, track.id],
  )
  const options: PopoverItemType[] = useMemo(() => {
    let options = getDownloadOptions(track, ext, url, handleDownloadClick)

    if (showSaveToDisco) {
      const saveToDiscoOptions = getSaveToDiscoOptions(
        track,
        nestedTracks,
        handleAddToDiscoClick,
      )
      options.push(...saveToDiscoOptions)
    }

    const viewPageLink = getViewPageLink(track)

    if (viewPageLink) {
      options.unshift({
        label: viewPageLink.caption,
        nodeRight: <Icon type="open-in-new-tab" />,
        type: 'link',
        link: {
          url: viewPageLink.url,
          target: '_blank',
        },
      })
    }

    return options
  }, [
    ext,
    url,
    handleAddToDiscoClick,
    handleDownloadClick,
    nestedTracks,
    showSaveToDisco,
    track,
  ])

  // Don't show menu for streaming-only playlists
  if (disableDownloads) {
    return null
  }

  const ellipsisMenuProps = {
    buttonSize,
    compact: true,
    disabled: track.virus || disableDownloads,
    options,
    popoverClassName: theme
      ? 'track-item-options__dropdown--with-theme'
      : undefined,
    onOptionClick,
  }

  if (tablet || compact) {
    return (
      <EllipsisMenu
        {...ellipsisMenuProps}
        hasArrow={false}
        icon="ellipsis-vertical"
      />
    )
  } else {
    return (
      !track.virus &&
      !disableDownloads &&
      url && (
        <EllipsisMenu
          {...ellipsisMenuProps}
          behavior={theme ? undefined : 'button-dropdown'}
          caption={theme ? undefined : `Download ${ext.toUpperCase()}`}
          hasArrow={theme ? false : options.length > 1}
          icon={theme ? 'ellipsis-vertical' : undefined}
          onToggle={() => {
            if (publicTrackItemRef && publicTrackItemRef.current) {
              // TODO: either delete public-list-item--active -or- refactor `classList.toggle` with HoverContext
              publicTrackItemRef.current.classList.toggle(
                'public-list-item--active',
              )
            }
          }}
        />
      )
    )
  }
}

export default React.memo<Props>(TrackItemOptions)
